import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle, ArrowRight } from "lucide-react";
import { api } from "@/lib/apiClient";

/**
 * /payment/success — landing page after a Stripe Checkout redirect.
 * Polls /api/payments/status/{session_id} until we see `paid` (or a
 * terminal failure), then shows the appropriate state and links the
 * user back to their care packages.
 */

const MAX_POLLS = 8;
const POLL_INTERVAL_MS = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | pending | failed | expired
  const [tx, setTx] = useState(null);
  const pollsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      pollsRef.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setTx(data);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState(data.payment_status);
          return;
        }
      } catch (_) {
        // Ignore transient network errors — keep polling.
      }
      if (pollsRef.current >= MAX_POLLS) {
        setState("pending");
        return;
      }
      setTimeout(tick, POLL_INTERVAL_MS);
    };

    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div
      data-testid="payment-success-page"
      className="min-h-screen bg-[color:var(--mano-bg)] flex items-center justify-center px-5 py-10"
    >
      <div className="max-w-md w-full rounded-3xl bg-white border border-[color:var(--mano-border)] p-8 space-y-5">
        {state === "polling" && (
          <div data-testid="payment-polling" className="text-center space-y-4">
            <Loader2 size={40} className="mx-auto animate-spin text-[color:var(--mano-primary)]" />
            <div className="font-display text-xl">Finalising your payment…</div>
            <p className="text-sm text-[color:var(--mano-ink-soft)]">
              Confirming with Stripe. This usually takes a couple of seconds.
            </p>
          </div>
        )}
        {state === "paid" && (
          <div data-testid="payment-paid" className="text-center space-y-4">
            <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 size={36} className="text-emerald-600" />
            </div>
            <div className="font-display text-2xl">Payment received</div>
            <p className="text-sm text-[color:var(--mano-ink-soft)]">
              {tx?.package_id
                ? "Your care package is now active — jump back in and start exploring."
                : "Your order is confirmed."}
            </p>
            <div className="flex flex-col gap-2 pt-2">
              <button
                data-testid="payment-go-therapies"
                onClick={() => nav("/therapies")}
                className="inline-flex items-center justify-center gap-2 h-11 rounded-full bg-[color:var(--mano-primary)] text-white font-semibold hover:bg-[color:var(--mano-primary-hover)]"
              >
                View my packages <ArrowRight size={14} />
              </button>
              <Link
                to="/home"
                data-testid="payment-go-home"
                className="inline-flex items-center justify-center gap-2 h-11 rounded-full border border-[color:var(--mano-border)]"
              >
                Back to home
              </Link>
            </div>
          </div>
        )}
        {state === "pending" && (
          <div data-testid="payment-pending" className="text-center space-y-4">
            <Loader2 size={40} className="mx-auto text-[color:var(--mano-primary)]" />
            <div className="font-display text-xl">Payment still processing</div>
            <p className="text-sm text-[color:var(--mano-ink-soft)]">
              Stripe hasn't confirmed yet. You'll see the package activate
              on your Therapies page as soon as it clears.
            </p>
            <Link
              to="/therapies"
              data-testid="payment-check-therapies"
              className="inline-flex items-center justify-center gap-2 h-11 rounded-full bg-[color:var(--mano-primary)] text-white font-semibold px-6"
            >
              Go to Therapies
            </Link>
          </div>
        )}
        {(state === "failed" || state === "expired") && (
          <div data-testid={`payment-${state}`} className="text-center space-y-4">
            <div className="mx-auto h-16 w-16 rounded-full bg-amber-100 flex items-center justify-center">
              <AlertTriangle size={32} className="text-amber-700" />
            </div>
            <div className="font-display text-xl">
              {state === "failed" ? "Payment didn't go through" : "Session expired"}
            </div>
            <p className="text-sm text-[color:var(--mano-ink-soft)]">
              No charge was made. You can retry from the Therapies page.
            </p>
            <Link
              to="/therapies"
              data-testid="payment-retry"
              className="inline-flex items-center justify-center gap-2 h-11 rounded-full bg-[color:var(--mano-primary)] text-white font-semibold px-6"
            >
              Back to Therapies
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
